import type { DialogProps } from "@radix-ui/react-dialog";
import type { PropsWithChildren } from "react";
import { useState } from "react";

import ActionButton from "@/components/Button/ActionButton";
import { VStack } from "@/components/Layout";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { WorkerType } from "@/constants/workerType";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { defaultNS, useTranslation } from "@/lib/i18n";
import EmailFormItem from "@/routes/_staff/settings/persons/_list/_account/-components/TabFilterBar/AddPersonToSendInvitationDialogEmailFormItem";
import NameFormItem from "@/routes/_staff/settings/persons/_list/_account/-components/TabFilterBar/AddPersonToSendInvitationDialogNameFormItem";
import WorkerTypeFormItem from "@/routes/_staff/settings/persons/_list/_account/-components/TabFilterBar/AddPersonToSendInvitationDialogWorkerTypeFormItem";
import type { AddNewPersonOption } from "@/routes/_staff/settings/persons/_list/_account/-types";

interface FormErrors {
  givenName?: string;
  familyName?: string;
  middleName?: string;
  email?: string;
  workerType?: string;
  workerTypeMasterId?: string;
  submission?: string;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type Props = PropsWithChildren<
  Omit<DialogProps, "onOpenChange"> & {
    onAdd: (value: AddNewPersonOption) => void;
    contractFrom: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    workerType: WorkerType;
  }
>;

const StartOperationTaskAddPersonDialog = (props: Props) => {
  const { children, contractFrom, onOpenChange, open, workerType, ...rest } =
    props;
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "features.operationPackage.addPersonDialog",
  });
  const tc = useCommonTranslation();

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [email, setEmail] = useState("");
  const [workerTypeMaster, setWorkerTypeMaster] = useState({
    id: "",
    name: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!givenName) {
      newErrors.givenName = "givenName";
    }
    if (!familyName) {
      newErrors.familyName = "familyName";
    }
    if (email && !EMAIL_REGEX.test(email)) {
      newErrors.email = "email";
    }
    if (!workerType) {
      newErrors.workerType = "workerType";
    }
    if (!workerTypeMaster.id) {
      newErrors.workerTypeMasterId = "workerTypeMasterId";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      props.onAdd({
        workerType,
        workerTypeMasterId: workerTypeMaster.id,
        givenName,
        familyName,
        middleName,
        email,
        contractFrom,
      });

      // フォームをリセット
      setGivenName("");
      setFamilyName("");
      setMiddleName("");
      setEmail("");
      setWorkerTypeMaster({ id: "", name: "" });
      setErrors({});
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog {...rest} open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </DialogHeader>
        <VStack className="gap-4">
          <WorkerTypeFormItem
            fixedWorkerType={true}
            selectedWorkerType={workerType}
            setSelectedWorkerType={() => {}}
            selectedWorkerTypeMaster={workerTypeMaster}
            setSelectedWorkerTypeMaster={setWorkerTypeMaster}
            errors={{
              workerType: errors.workerType ? "workerType" : undefined,
              workerTypeMasterId: errors.workerTypeMasterId
                ? "workerTypeMasterId"
                : undefined,
            }}
          />

          <NameFormItem
            givenName={givenName}
            setGivenName={setGivenName}
            familyName={familyName}
            setFamilyName={setFamilyName}
            middleName={middleName}
            setMiddleName={setMiddleName}
            errors={{
              givenName: errors.givenName ? "givenName" : undefined,
              familyName: errors.familyName ? "familyName" : undefined,
            }}
          />

          <EmailFormItem
            email={email}
            setEmail={setEmail}
            errors={{
              email: errors.email ? "email" : undefined,
            }}
            isOptional={true}
          />
        </VStack>

        <DialogFooter>
          <DialogClose asChild>
            <ActionButton variant="ghost">{tc("action.cancel")}</ActionButton>
          </DialogClose>
          <ActionButton onClick={handleSubmit}>{tc("action.add")}</ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StartOperationTaskAddPersonDialog;
